import React, { MouseEvent, useEffect, useId, useState } from 'react';
import { Box, Chip, Divider, Link, Stack, Tooltip, TooltipProps, Typography } from '@mui/material';
import { StarRate, CorporateFare, Home, Tour, Public, SyncAlt } from '@mui/icons-material';
import ExtensionIcon from '@mui/icons-material/Extension';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import EventSeatOutlinedIcon from '@mui/icons-material/EventSeatOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import strings from 'common/Translation/Translate';
import moment from 'moment';
import Currency from 'utils/hooks/getCurrency';
import { formatGuestCountCompact } from 'utils/validators/HelperFunctions';

export interface BookingTooltipProps {
  eventType?: string;
  eventSubType?: string;
  numberOfGuests?: number;
  expectedGuests?: number;
  guaranteedGuests?: number;
  customerName?: string;
  customerPhone?: string;
  companyName?: string;
  venueName?: string;
  placeOfEvent?: string;
  layoutName?: string;
  pricePackageName?: string;
  revenue?: number | null;
  createdByName?: string;
  createdAt?: number | string | Date;
  salesPerson?: string;
  status?: string;
  statusColor?: string;
  customerBooking?: boolean;
  corporateBooking?: boolean;
  roomsAdded?: boolean;
  beoReleased?: boolean;
  externalBooking?: boolean;
  idsBookingRef?: boolean | string;
  programmingEvent?: boolean;
  startDate?: Date;
  endDate?: Date;
  children: React.ReactElement;
  placement?: TooltipProps['placement'];
  onViewDetails?: () => void;
}

const dash = '—';

type ExclusiveListener = (activeId: string | null) => void;

let activeTooltipId: string | null = null;
const exclusiveListeners = new Set<ExclusiveListener>();

const subscribeExclusiveTooltip = (listener: ExclusiveListener) => {
  exclusiveListeners.add(listener);
  return () => {
    exclusiveListeners.delete(listener);
  };
};

const claimExclusiveTooltip = (id: string) => {
  if (activeTooltipId === id) {
    return;
  }
  activeTooltipId = id;
  exclusiveListeners.forEach(listener => listener(id));
};

const releaseExclusiveTooltip = (id: string) => {
  if (activeTooltipId !== id) {
    return;
  }
  activeTooltipId = null;
  exclusiveListeners.forEach(listener => listener(null));
};

const hasText = (value?: string | null) => Boolean(value && String(value).trim());

const FLAG_CHIP_DEFAULT = { bg: '#F2F4F7', color: '#344054' };

const FLAG_CHIP_COLORS: Record<string, { bg: string; color: string }> = {
  ids: { bg: '#E3F2FD', color: '#0D47A1' },
  customer: { bg: '#FFF4D6', color: '#B8860B' },
  beo: { bg: '#E8F5E9', color: '#2E7D32' },
};

const FlagChip = ({
  icon,
  label,
  colorKey,
}: {
  icon?: React.ReactNode;
  label: string;
  colorKey?: keyof typeof FLAG_CHIP_COLORS;
}) => {
  const palette = (colorKey && FLAG_CHIP_COLORS[colorKey]) || FLAG_CHIP_DEFAULT;
  return (
    <Chip
      size="small"
      {...(icon ? { icon: icon as any } : {})}
      label={label}
      variant="filled"
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 600,
        bgcolor: palette.bg,
        color: palette.color,
        '& .MuiChip-icon': { fontSize: 14, ml: 0.5, color: 'inherit' },
      }}
    />
  );
};

const iconSx = { fontSize: 16, color: 'text.secondary', flexShrink: 0, mt: '1px' };

const IconLine = ({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) => (
  <Stack direction="row" alignItems="flex-start" spacing={0.75} sx={{ minWidth: 0 }}>
    {icon}
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', lineHeight: 1.35, wordBreak: 'break-word' }}>
      {children}
    </Typography>
  </Stack>
);

const SectionCell = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
      {icon}
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: 'text.secondary',
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        }}>
        {label}
      </Typography>
    </Stack>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', wordBreak: 'break-word' }}>{value}</Typography>
  </Box>
);

const BookingTooltip: React.FC<BookingTooltipProps> = ({
  eventType,
  eventSubType,
  numberOfGuests,
  expectedGuests,
  guaranteedGuests,
  customerName,
  companyName,
  venueName,
  placeOfEvent,
  layoutName,
  pricePackageName,
  revenue,
  createdByName,
  createdAt,
  salesPerson,
  status,
  statusColor,
  customerBooking,
  corporateBooking,
  roomsAdded,
  beoReleased,
  externalBooking,
  idsBookingRef,
  programmingEvent,
  startDate,
  endDate,
  children,
  placement = 'top-start',
  onViewDetails,
}) => {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeExclusiveTooltip(activeId => {
      if (activeId !== tooltipId) {
        setOpen(false);
      }
    });
    return () => {
      unsubscribe();
      releaseExclusiveTooltip(tooltipId);
    };
  }, [tooltipId]);

  if (status === 'BLOCKED') {
    return children;
  }

  const venueLabel = venueName || placeOfEvent;
  const createdName = createdByName || salesPerson;
  const createdTime = createdAt
    ? moment(createdAt).format('D MMM YYYY, HH:mm')
    : startDate
      ? moment(startDate).format('D MMM YYYY, HH:mm')
      : '';

  const segmentLine = [eventType, eventSubType].filter(hasText).join(' · ');
  const expectedCount = expectedGuests ?? numberOfGuests;
  const hasGuestData = guaranteedGuests != null || expectedCount != null;
  const guestLine = hasGuestData
    ? formatGuestCountCompact(guaranteedGuests ?? expectedCount, expectedCount)
    : '';

  const dateLabel = startDate ? moment(startDate).format('D MMM YYYY') : '';
  const timeLabel = (() => {
    if (!startDate) return '';
    const startTime = moment(startDate).format('HH:mm');
    if (!endDate) return startTime;
    return `${startTime} - ${moment(endDate).format('HH:mm')}`;
  })();

  const showSeating = hasText(layoutName);
  const showGuests = Boolean(guestLine);
  const showPricePackage = hasText(pricePackageName);
  const showRevenue = revenue != null;
  const showSeatingGuestsSection = showSeating || showGuests;
  const showPriceSection = showPricePackage || showRevenue;

  const flags: {
    show: boolean;
    icon?: React.ReactNode;
    label: string;
    colorKey?: keyof typeof FLAG_CHIP_COLORS;
  }[] = [
    {
      show: Boolean(customerBooking),
      icon: <StarRate />,
      label: strings.calendar_tooltip_flag_customer,
      colorKey: 'customer',
    },
    { show: Boolean(corporateBooking), icon: <CorporateFare />, label: strings.calendar_tooltip_flag_corporate },
    { show: Boolean(beoReleased), icon: <Tour />, label: strings.calendar_tooltip_flag_beo, colorKey: 'beo' },
    { show: Boolean(roomsAdded), icon: <Home />, label: strings.calendar_tooltip_flag_rooms },
    {
      show: Boolean(externalBooking),
      icon: <Public />,
      label: strings.calendar_tooltip_flag_external,
      colorKey: 'ids',
    },
    { show: Boolean(idsBookingRef), icon: <SyncAlt />, label: strings.calendar_tooltip_flag_ids, colorKey: 'ids' },
    { show: Boolean(programmingEvent), icon: <ExtensionIcon />, label: strings.calendar_tooltip_flag_programming },
  ];

  const stopCalendarClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleViewDetails = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onViewDetails?.();
  };

  const claimExclusive = () => {
    claimExclusiveTooltip(tooltipId);
  };

  const handleOpen = () => {
    claimExclusiveTooltip(tooltipId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    releaseExclusiveTooltip(tooltipId);
  };

  const trigger = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<any>, {
        onMouseEnter: (e: React.MouseEvent) => {
          claimExclusive();
          (children.props as any).onMouseEnter?.(e);
        },
      })
    : children;

  return (
    <Tooltip
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      disableInteractive={false}
      enterDelay={200}
      leaveDelay={400}
      placement={placement}
      arrow
      TransitionProps={{ timeout: { enter: 120, exit: 0 } }}
      title={
        <Box
          onMouseDown={stopCalendarClick}
          onClick={stopCalendarClick}
          sx={{ minWidth: 280, maxWidth: 360, py: 0.25, userSelect: 'text' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, pr: 1 }}>
              {customerName || dash}
            </Typography>
            {status && (
              <Chip
                size="small"
                label={String(status).replace(/_/g, ' ')}
                sx={{
                  height: 22,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'capitalize',
                  bgcolor: statusColor || '#667085',
                  color: '#fff',
                  flexShrink: 0,
                }}
              />
            )}
          </Stack>
          {hasText(companyName) && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>{companyName}</Typography>
          )}

          {(segmentLine || hasText(venueLabel) || dateLabel) && <Divider sx={{ my: 1 }} />}

          <Stack spacing={0.75}>
            {segmentLine && (
              <IconLine icon={<CelebrationOutlinedIcon sx={iconSx} />}>{segmentLine}</IconLine>
            )}
            {hasText(venueLabel) && <IconLine icon={<PlaceOutlinedIcon sx={iconSx} />}>{venueLabel}</IconLine>}
            {(dateLabel || timeLabel) && (
              <Stack direction="row" alignItems="center" flexWrap="wrap" spacing={0.75}>
                {dateLabel && <IconLine icon={<CalendarMonthOutlinedIcon sx={iconSx} />}>{dateLabel}</IconLine>}
                {dateLabel && timeLabel && (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>|</Typography>
                )}
                {timeLabel && <IconLine icon={<ScheduleOutlinedIcon sx={iconSx} />}>{timeLabel}</IconLine>}
              </Stack>
            )}
          </Stack>

          {showSeatingGuestsSection && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: showSeating && showGuests ? '1fr 1fr' : '1fr',
                  columnGap: 1.5,
                }}>
                {showSeating && (
                  <SectionCell
                    icon={<EventSeatOutlinedIcon sx={{ ...iconSx, fontSize: 14 }} />}
                    label={strings.calendar_tooltip_seating_style}
                    value={layoutName}
                  />
                )}
                {showGuests && (
                  <SectionCell
                    icon={<GroupsOutlinedIcon sx={{ ...iconSx, fontSize: 14 }} />}
                    label={strings.calendar_tooltip_guests}
                    value={guestLine}
                  />
                )}
              </Box>
            </>
          )}

          {showPriceSection && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: showPricePackage && showRevenue ? '1fr 1fr' : '1fr',
                  columnGap: 1.5,
                }}>
                {showPricePackage && (
                  <SectionCell
                    icon={<LocalOfferOutlinedIcon sx={{ ...iconSx, fontSize: 14 }} />}
                    label={strings.calendar_tooltip_price_package}
                    value={pricePackageName}
                  />
                )}
                {showRevenue && (
                  <SectionCell
                    icon={<PaymentsOutlinedIcon sx={{ ...iconSx, fontSize: 14 }} />}
                    label={strings.calendar_tooltip_revenue}
                    value={Currency(revenue)}
                  />
                )}
              </Box>
            </>
          )}

          {flags.some(f => f.show) && (
            <>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {flags
                  .filter(f => f.show)
                  .map(f => (
                    <FlagChip key={f.label} icon={f.icon} label={f.label} colorKey={f.colorKey} />
                  ))}
              </Stack>
            </>
          )}

          <Divider sx={{ my: 1 }} />

          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {strings.booking_details_sales_person}
            </Box>{' '}
            {[createdName, createdTime].filter(Boolean).join(', ') || dash}
          </Typography>

          {onViewDetails && (
            <>
              <Divider sx={{ my: 1 }} />
              <Link
                component="button"
                underline="hover"
                onClick={handleViewDetails}
                onMouseDown={stopCalendarClick}
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                  background: 'none',
                  border: 0,
                  p: 0,
                  fontFamily: 'inherit',
                }}>
                {strings.calendar_tooltip_view_details}
              </Link>
            </>
          )}
        </Box>
      }
      PopperProps={{
        modifiers: [
          { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
          { name: 'offset', options: { offset: [0, 8] } },
        ],
        sx: {
          zIndex: theme => theme.zIndex.modal + 2,
          pointerEvents: 'auto',
        },
      }}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: '#fff',
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: '0 8px 24px rgba(16, 24, 40, 0.18)',
            border: '1px solid #EEF2F6',
            borderRadius: '10px',
            maxWidth: 380,
            p: 1.5,
            pointerEvents: 'auto',
          },
        },
        arrow: {
          sx: { color: '#fff' },
        },
      }}>
      {trigger}
    </Tooltip>
  );
};

export default BookingTooltip;
